import type { DispatchMetric } from '../../api/types';
import { kpiHeaderLabel, kpiHeaderRank, kpiPlacement, metricTypeOf, normalizeKpiHeader } from './metricMeta';

/**
 * Where each card sits, and what a drag is allowed to do to that.
 *
 * The order is dispatch's `priority`, descending, and it is SHARED — the
 * Flutter dashboard reads the same column, so a card dragged here moves there
 * too. That is the point: one arrangement, not one per front end.
 *
 * Priority carries ties and duplicates from years of hand-set numbers, so the
 * sort tie-breaks on id. Without that a page of equal priorities comes back in
 * whatever order the query happened to return, and a drag appears to do
 * nothing every second attempt.
 */

export type GroupKind = 'kpi' | 'chart';

export interface MetricGroup {
  key: string;
  kind: GroupKind;
  /** KPI groups sit above or below the charts; the charts are the middle. */
  placement: 'above' | 'charts' | 'below';
  /** Printed over the group, or null for the headline band, which needs none. */
  label: string | null;
  metrics: DispatchMetric[];
}

/**
 * Which group a metric belongs to.
 *
 * A chart is a chart wherever it sits; a KPI belongs to whatever its
 * `arrangeBy` header says, including a header somebody typed. This is also the
 * boundary a drag may not cross — see `moveMetric`.
 */
export const groupKeyOf = (metric: DispatchMetric): string =>
  metricTypeOf(metric.type) === 'kpi' ? `kpi:${normalizeKpiHeader(metric.arrangeBy)}` : 'chart';

/** dispatch's own ordering: priority descending, id ascending to break ties. */
export const byPriority = (metrics: DispatchMetric[]): DispatchMetric[] =>
  [...metrics].sort((a, b) => b.priority - a.priority || a.id - b.id);

/**
 * The list in the order the reader last dragged it into, before the server has
 * confirmed.
 *
 * Ids in `order` lead, in that order; anything the order does not name keeps
 * its priority position behind them. That second half matters on a page left
 * open while a colleague adds a metric — the new card appears rather than
 * vanishing because a stale order did not mention it.
 */
export const applyOrder = (metrics: DispatchMetric[], order: number[] | null): DispatchMetric[] => {
  const sorted = byPriority(metrics);
  if (!order || !order.length) return sorted;
  const byId = new Map(sorted.map((metric) => [metric.id, metric]));
  const named: DispatchMetric[] = [];
  for (const id of order) {
    const metric = byId.get(id);
    if (metric) {
      named.push(metric);
      byId.delete(id);
    }
  }
  return [...named, ...byId.values()];
};

/**
 * Move `fromId` onto `toId`'s slot, returning the new global id order.
 *
 * Refuses across groups, which is the one freedom the grouping costs. A KPI's
 * group is its stored header and a chart's is that it is a chart, so dragging
 * a KPI into the chart grid would be claiming to change what the metric IS —
 * which is an edit, and belongs in the edit dialog rather than in a drag.
 *
 * Null rather than the unchanged list, so the caller can tell a refused drop
 * from a no-op one and skip the write.
 */
export const moveMetric = (
  metrics: DispatchMetric[],
  fromId: number,
  toId: number,
): number[] | null => {
  if (fromId === toId) return null;
  const from = metrics.find((metric) => metric.id === fromId);
  const to = metrics.find((metric) => metric.id === toId);
  if (!from || !to) return null;
  if (groupKeyOf(from) !== groupKeyOf(to)) return null;

  const ids = metrics.map((metric) => metric.id);
  const fromIndex = ids.indexOf(fromId);
  const toIndex = ids.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0) return null;
  const next = [...ids];
  next.splice(fromIndex, 1);
  next.splice(next.indexOf(toId) + (toIndex > fromIndex ? 1 : 0), 0, fromId);
  return next;
};

/**
 * The groups, in the order the page reads top to bottom.
 *
 * KPI rows first (the headline band, then Dispatch, then anything typed), the
 * charts in the middle, and the Below Charts row last — which is the placement
 * the dashboard's own presets encode, so a tenant who arranged their page over
 * there opens this one and finds it the same way up.
 */
export const groupMetrics = (metrics: DispatchMetric[]): MetricGroup[] => {
  const kpis = new Map<string, MetricGroup>();
  const charts: DispatchMetric[] = [];

  for (const metric of metrics) {
    if (metricTypeOf(metric.type) !== 'kpi') {
      charts.push(metric);
      continue;
    }
    const header = normalizeKpiHeader(metric.arrangeBy);
    const key = `kpi:${header}`;
    const existing = kpis.get(key);
    if (existing) {
      existing.metrics.push(metric);
      continue;
    }
    kpis.set(key, {
      key,
      kind: 'kpi',
      placement: kpiPlacement(header),
      label: kpiHeaderLabel(header),
      metrics: [metric],
    });
  }

  const rank = (group: MetricGroup) => kpiHeaderRank(group.key.slice('kpi:'.length));
  const sortRows = (rows: MetricGroup[]) =>
    rows.sort((a, b) => rank(a) - rank(b) || (a.label ?? '').localeCompare(b.label ?? ''));

  const rows = [...kpis.values()];
  const above = sortRows(rows.filter((group) => group.placement === 'above'));
  const below = sortRows(rows.filter((group) => group.placement === 'below'));

  const out: MetricGroup[] = [...above];
  if (charts.length) {
    out.push({ key: 'chart', kind: 'chart', placement: 'charts', label: null, metrics: charts });
  }
  return [...out, ...below];
};

/**
 * The groups in render order, cut into consecutive runs of one kind.
 *
 * KPI bands flow into a wrapping row, so two small groups that fit one line
 * share it instead of taking a line each — which on a page of two-card groups
 * is most of the screen spent on nothing. Charts keep a section each, because a
 * chart grid already fills its width.
 *
 * A RUN rather than "all the KPI groups": `groupMetrics` puts the charts
 * between the bands that sit above them and the ones that sit below, and
 * collapsing that into one row would move a Below Charts group above the
 * charts — which is the one thing its name promises it will not do.
 */
export const bandRuns = (groups: MetricGroup[]): { key: string; kind: GroupKind; groups: MetricGroup[] }[] => {
  const runs: { key: string; kind: GroupKind; groups: MetricGroup[] }[] = [];
  for (const group of groups) {
    const last = runs[runs.length - 1];
    if (last && last.kind === group.kind) last.groups.push(group);
    else runs.push({ key: `run:${group.key}`, kind: group.kind, groups: [group] });
  }
  return runs;
};
